import { ConversationInsightsFiltersV1Schema } from "@redotech/redo-model/conversation-filters/conversation-insights-filters";
import { DateGroupingField } from "@redotech/redo-model/conversation-insights";
import * as z from "zod/v4";

const SentimentBreakdown_ = z.object({
  positive: z.number(),
  neutral: z.number(),
  negative: z.number(),
});

export const SentimentBreakdown: z.ZodType<SentimentBreakdown> =
  SentimentBreakdown_;
export interface SentimentBreakdown
  extends z.infer<typeof SentimentBreakdown_> {}

const DailySentimentAnalytics_ = z.object({
  date: z.string(), // ISO date string (YYYY-MM-DD)
  sentimentBreakdown: SentimentBreakdown,
  totalConversations: z.number(),
});

export const DailySentimentAnalytics: z.ZodType<DailySentimentAnalytics> =
  DailySentimentAnalytics_;
export interface DailySentimentAnalytics
  extends z.infer<typeof DailySentimentAnalytics_> {}

const CategoryBreakdown_ = z.record(z.string(), z.number());

export const CategoryBreakdown: z.ZodType<CategoryBreakdown> =
  CategoryBreakdown_;
export interface CategoryBreakdown extends z.infer<typeof CategoryBreakdown_> {}

const DailyCategoryAnalytics_ = z.object({
  date: z.string(), // ISO date string (YYYY-MM-DD)
  categoryBreakdown: CategoryBreakdown,
  totalConversations: z.number(),
});

export const DailyCategoryAnalytics: z.ZodType<DailyCategoryAnalytics> =
  DailyCategoryAnalytics_;
export interface DailyCategoryAnalytics
  extends z.infer<typeof DailyCategoryAnalytics_> {}

const SentimentTotals_ = z.object({
  totalPositive: z.number(),
  totalNeutral: z.number(),
  totalNegative: z.number(),
});

export const SentimentTotals: z.ZodType<SentimentTotals> = SentimentTotals_;
export interface SentimentTotals extends z.infer<typeof SentimentTotals_> {}

const CategoryTotals_ = z.record(z.string(), z.number());

export const CategoryTotals: z.ZodType<CategoryTotals> = CategoryTotals_;
export interface CategoryTotals extends z.infer<typeof CategoryTotals_> {}

const DailyToolCallAnalytics_ = z.object({
  date: z.string(), // ISO date string (YYYY-MM-DD)
  toolCallSuccesses: z.number(),
  toolCallErrors: z.number(),
  totalToolCalls: z.number(),
  totalConversations: z.number(),
});

export const DailyToolCallAnalytics: z.ZodType<DailyToolCallAnalytics> =
  DailyToolCallAnalytics_;
export interface DailyToolCallAnalytics
  extends z.infer<typeof DailyToolCallAnalytics_> {}

const ToolCallTotals_ = z.object({
  totalSuccesses: z.number(),
  totalErrors: z.number(),
  totalToolCalls: z.number(),
});

export const ToolCallTotals: z.ZodType<ToolCallTotals> = ToolCallTotals_;
export interface ToolCallTotals extends z.infer<typeof ToolCallTotals_> {}

const DailyToolAnalytics_ = z.object({
  date: z.string(), // ISO date string (YYYY-MM-DD)
  toolAnalysis: z.record(
    z.string(),
    z.object({
      total: z.number(),
      successes: z.number(),
      errorCount: z.number(),
    }),
  ),
  totalConversations: z.number(),
});

export const DailyToolAnalytics: z.ZodType<DailyToolAnalytics> =
  DailyToolAnalytics_;
export interface DailyToolAnalytics
  extends z.infer<typeof DailyToolAnalytics_> {}

const ToolAnalysisTotals_ = z.record(
  z.string(),
  z.object({
    total: z.number(),
    successes: z.number(),
    errorCount: z.number(),
  }),
);

export const ToolAnalysisTotals: z.ZodType<ToolAnalysisTotals> =
  ToolAnalysisTotals_;
export interface ToolAnalysisTotals
  extends z.infer<typeof ToolAnalysisTotals_> {}

const DailyAssigneeSummaryAnalytics_ = z.object({
  date: z.string(), // ISO date string (YYYY-MM-DD)
  assigneeSummaryBreakdown: z.record(z.string(), z.number()),
  totalConversations: z.number(),
});

export const DailyAssigneeSummaryAnalytics: z.ZodType<DailyAssigneeSummaryAnalytics> =
  DailyAssigneeSummaryAnalytics_;
export interface DailyAssigneeSummaryAnalytics
  extends z.infer<typeof DailyAssigneeSummaryAnalytics_> {}

const AssigneeSummaryTotals_ = z.record(z.string(), z.number());

export const AssigneeSummaryTotals: z.ZodType<AssigneeSummaryTotals> =
  AssigneeSummaryTotals_;
export interface AssigneeSummaryTotals
  extends z.infer<typeof AssigneeSummaryTotals_> {}

const CategorySentimentCombinedTotals_ = z.record(
  z.string(),
  z.object({
    positive: z.number(),
    neutral: z.number(),
    negative: z.number(),
    total: z.number(),
  }),
);

export const CategorySentimentCombinedTotals: z.ZodType<CategorySentimentCombinedTotals> =
  CategorySentimentCombinedTotals_;
export interface CategorySentimentCombinedTotals
  extends z.infer<typeof CategorySentimentCombinedTotals_> {}

const ToolErrorBreakdownTotals_ = z.record(
  z.string(),
  z.record(z.string(), z.number()),
);

export const ToolErrorBreakdownTotals: z.ZodType<ToolErrorBreakdownTotals> =
  ToolErrorBreakdownTotals_;
export interface ToolErrorBreakdownTotals
  extends z.infer<typeof ToolErrorBreakdownTotals_> {}

const ConversationInsightsAnalyticsData_ = z.object({
  sentimentByDay: z.array(DailySentimentAnalytics),
  conversationCategoriesByDay: z.array(DailyCategoryAnalytics),
  toolCallAnalyticsByDay: z.array(DailyToolCallAnalytics),
  toolAnalyticsByDay: z.array(DailyToolAnalytics),
  assigneeSummaryByDay: z.array(DailyAssigneeSummaryAnalytics),
  sentimentTotals: SentimentTotals,
  conversationCategoriesTotals: CategoryTotals,
  toolCallTotals: ToolCallTotals,
  toolAnalysisTotals: ToolAnalysisTotals,
  assigneeSummaryTotals: AssigneeSummaryTotals,
  categorySentimentCombinedTotals: CategorySentimentCombinedTotals,
  toolErrorBreakdownTotals: ToolErrorBreakdownTotals,
});

export const ConversationInsightsAnalyticsData: z.ZodType<ConversationInsightsAnalyticsData> =
  ConversationInsightsAnalyticsData_;
export interface ConversationInsightsAnalyticsData
  extends z.infer<typeof ConversationInsightsAnalyticsData_> {}

export const getConversationInsightsAnalytics = {
  name: "getConversationInsightsAnalytics" as const,
  input: z.object({
    filters: ConversationInsightsFiltersV1Schema.optional(),
    dateGroupingField: DateGroupingField.optional(),
  }),
  output: ConversationInsightsAnalyticsData,
};
